import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, json } from '../api/client'
import type { Aspect, Clip, ClipsOverview } from '../api/types'
import { episodeKey, refreshEpisode } from './useShows'

const clipsKey = (ws: string, id: string) => [...episodeKey(ws, id), 'clips'] as const

export function useClips(ws: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: clipsKey(ws, id),
    queryFn: () => api<ClipsOverview>(`/workspaces/${ws}/episodes/${id}/clips`),
    enabled,
  })
}

/** Find moments worth clipping; an instruction revises the last suggestions. */
export function useSuggestClips(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { instruction?: string }) =>
      api<{ job_id: string }>(`/workspaces/${ws}/episodes/${id}/clips/suggest`, {
        method: 'POST',
        body: json({ instruction: input.instruction || null }),
      }),
    onSuccess: () => refreshEpisode(queryClient, ws, id),
  })
}

/** Make a clip from a range picked in the transcript. */
export function useCreateClip(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { start_ms: number; end_ms: number; title?: string }) =>
      api<Clip>(`/workspaces/${ws}/episodes/${id}/clips`, { method: 'POST', body: json(input) }),
    // The episode's key covers its clips and what rendering each one costs.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: episodeKey(ws, id) }),
  })
}

export function useRenderClip(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { clipId: string; aspect: Aspect }) =>
      api<{ job_id: string }>(`/workspaces/${ws}/episodes/${id}/clips/${input.clipId}/render`, {
        method: 'POST',
        body: json({ aspect: input.aspect }),
      }),
    onSuccess: () => refreshEpisode(queryClient, ws, id),
  })
}

export function useDeleteClip(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (clipId: string) => api<void>(`/workspaces/${ws}/episodes/${id}/clips/${clipId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clipsKey(ws, id) }),
  })
}

export function useMakeThumbnail(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ job_id: string }>(`/workspaces/${ws}/episodes/${id}/thumbnail`, { method: 'POST' }),
    onSuccess: () => refreshEpisode(queryClient, ws, id),
  })
}
